import type {
  SearchFilterCondition,
  SearchFilterField,
  SearchFilterOperator,
  SearchFilters,
  SearchFilterStrategy,
  SearchFilterValue,
} from 'lib-shared-types-api';
import memoizeOne from 'memoize-one';

import { enrichStaticFilters } from './enrichStaticFilters';
import { isNullishFilterOperator } from './isNullishFilterOperator';

const DELIMITER = {
  FIELD: '~',
  VALUES: ',',
  CONDITIONS: ';',
};

/**
 * Generates a URL-friendly string from a list of search filter conditions.
 *
 * @example
 *   { strategy: 'all', conditions: [ { field: 'status', operator: 'isOne',
 *   values: [{ label: 'Open', value: 'open' }] }, { field: 'tags', operator:
 *   'includesAll', values: [{ label: 'Sales', value: '1a2b3c' }, {{ label:
 *   'Important', value: '4d5e6f' }] } ] } -->
 *   strategy~all;status~is_one~open;tags~includes_all~1a2b3c,4d5e6f
 */
export const convertFiltersObjectToString = memoizeOne(
  (filters?: SearchFilters): string => {
    if (!filters?.conditions.length) return '';

    const result = filters.conditions.reduce<string[]>(
      (acc, { field, operator, values }) => {
        // Ignore conditions that are not IS_EMPTY or IS_NOT_EMPTY and that have no values
        if (!values.length && !isNullishFilterOperator(operator)) {
          return acc;
        }

        const stringifiedValues = values.length
          ? values.map(({ value }) => value).join(DELIMITER.VALUES)
          : undefined;

        const string = [field, operator, stringifiedValues]
          .filter(Boolean)
          .join(DELIMITER.FIELD);

        acc.push(string);

        return acc;
      },
      [],
    );

    if (result.length) {
      result.unshift(`strategy${DELIMITER.FIELD}${filters.strategy}`);
    }
    return result.join(DELIMITER.CONDITIONS);
  },
);

/**
 * Generates a list of search filter conditions from a URL-friendly string.
 *
 * @example
 *   strategy~all;status~is_one~open;tags~includes_all~1a2b3c,4d5e6f --> { strategy: 'all', conditions: [ { field: 'status', operator: 'isOne',
 *   values: [{ label: 'Open', value: 'open' }] }, { field: 'tags', operator:
 *   'includesAll', values: [{ label: 'Sales', value: '1a2b3c' }, {{ label:
 *   'Important', value: '4d5e6f' }] } ] }
 */
export const convertFiltersStringToObject = memoizeOne(
  (filters?: string): SearchFilters => {
    const initialOutput: SearchFilters = {
      strategy: 'all',
      conditions: [],
    };

    if (filters) {
      const [strategyStr, ...conditions] = filters.split(DELIMITER.CONDITIONS);
      const strategy = strategyStr.split(
        DELIMITER.FIELD,
      )[1] as SearchFilterStrategy;

      if (['all', 'any'].includes(strategy)) {
        initialOutput.strategy = strategy as SearchFilterStrategy;
      }

      const parsedFilters = conditions.reduce((acc, conditionString) => {
        const [field, operator, valuesStr] = conditionString.split(
          DELIMITER.FIELD,
        ) as [SearchFilterField, SearchFilterOperator, string];

        let values: SearchFilterValue[] = [];

        if (valuesStr) {
          values = valuesStr
            .split(DELIMITER.VALUES)
            .map((value) => ({ label: '', value }));
        }

        acc.conditions.push({
          field,
          operator,
          values,
        } as SearchFilterCondition);
        return acc;
      }, initialOutput);

      return enrichStaticFilters(parsedFilters);
    }

    return initialOutput;
  },
);
